import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitCommitHorizontal } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { CompareSheet } from './CompareSheet';
import { VivaGradeDialog } from './VivaGradeDialog';
import { WorkArtifactSheet } from './WorkArtifactSheet';
import {
  KIND_ICON,
  KIND_LABEL,
  SIGNAL_LABEL,
  SIGNAL_TONE,
  STATUS_LABEL,
  STATUS_TONE,
  formatWhen,
  hasSignal,
} from './workVocabulary';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  trainerApi,
  type ClusterKind,
  type WorkItemDetail,
  type WorkItemStudent,
  type WorkKind,
  type WorkRepoProvenance,
} from '@/lib/api/trainer';
import { cn } from '@/lib/cn';

/**
 * One piece of work, every student against it.
 *
 * The student feed answers "how is Priya doing". This answers "how did the
 * batch do on this" — the question that decides what to reteach, and the only
 * one that can show a copy, since similarity is a fact about two people doing
 * the same task and has nowhere to live on a page about one person.
 *
 * The two axes stay in separate columns throughout: whether the work got
 * finished, and how it came to exist. A student can be done and worth asking
 * about; a student can be stuck having written every line themselves. Merging
 * those into one "quality" column would collapse the distinction the whole
 * feature exists to preserve.
 */

/** Filters, not scores — the same vocabulary the attention queue uses. */
export type Filter = 'all' | 'not_started' | 'in_progress' | 'to_ask' | 'matches';

const FILTERS: { key: Filter; label: string; kinds?: WorkKind[] }[] = [
  { key: 'all', label: 'Everyone' },
  { key: 'not_started', label: 'Not started' },
  { key: 'in_progress', label: 'In progress' },
  // A quiz is a set of answers with no process behind it, so this can only ever
  // return nobody there. Practice and repository work both have something to
  // say, by different means.
  { key: 'to_ask', label: 'To ask about', kinds: ['practice', 'assignment', 'project'] },
  // Similarity compares code, and only practice stores any.
  { key: 'matches', label: 'Shares code', kinds: ['practice'] },
];

/**
 * Mirrors the server's definition of "worth asking about".
 *
 * Kept deliberately in step with worthAsking() in the Go side: a chip that
 * counts differently from the header above it is worse than no chip. "One
 * sitting" is absent from both — doing a small assignment in an evening is what
 * most students do, and a filter that returns most of the batch is one people
 * stop pressing.
 */
export function matchesFilter(s: WorkItemStudent, f: Filter): boolean {
  switch (f) {
    case 'not_started':
      return s.status === 'not_started';
    case 'in_progress':
      return s.status === 'in_progress' || s.status === 'submitted';
    case 'to_ask': {
      const signal = s.integrity?.signal;
      if (signal === 'pasted' || signal === 'assisted' || signal === 'one_commit') return true;
      const repo = s.integrity?.repo;
      return !!repo && (repo.is_fork || repo.predates_assignment);
    }
    case 'matches':
      return s.cluster_id != null;
    default:
      return true;
  }
}

export function WorkItemDetailView({
  batchId,
  assessmentId,
  readOnly = false,
}: {
  batchId: string;
  assessmentId: string;
  /**
   * Hides the one control here that writes. Everything else on this screen is
   * a read and stays: an administrator who cannot open the code cannot verify
   * anything, which is the whole reason they are here.
   */
  readOnly?: boolean;
}) {
  const [filter, setFilter] = useState<Filter>('all');
  // Which student's work is open, and which pair is being read side by side.
  const [viewing, setViewing] = useState<{ id: string; name: string } | null>(null);
  const [pair, setPair] = useState<{ a: string; b: string } | null>(null);
  // The student currently being marked in the room. Only ever set for a viva.
  const [grading, setGrading] = useState<{ id: string; name: string } | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['trainer', 'work-item', batchId, assessmentId],
    queryFn: async () => (await trainerApi.getWorkItem(batchId, assessmentId)).data,
  });

  const rows = useMemo(
    () => (data?.students ?? []).filter((s) => matchesFilter(s, filter)),
    [data, filter],
  );

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true">
        <span className="sr-only">Loading this piece of work…</span>
        <Skeleton className="h-16 w-full" />
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-muted-foreground">This work item could not be loaded.</p>
        <Button variant="link" onClick={() => void refetch()} className="mt-1">
          Try again
        </Button>
      </Card>
    );
  }

  const Icon = KIND_ICON[data.item.kind];
  // A viva is marked here and nowhere else: there is no submission, so the
  // grading inbox has nothing to show until a mark exists. read-only viewers
  // (an org admin) see the roster and the marks, but cannot enter one.
  const isViva = data.item.kind === 'viva';
  const canGrade = isViva && !readOnly;

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{KIND_LABEL[data.item.kind]}</span>
          {data.item.module_title && <span aria-hidden="true">·</span>}
          {data.item.module_title && <span className="truncate">{data.item.module_title}</span>}
          {data.item.topic_title && <span aria-hidden="true">·</span>}
          {data.item.topic_title && <span className="truncate">{data.item.topic_title}</span>}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">{data.item.title}</h2>
          {/* Only where there are repositories to read. */}
          {/* A viva has no repository to read either — nothing was handed in. */}
          {!readOnly && data.item.kind !== 'practice' && data.item.kind !== 'quiz' && !isViva && (
            <CheckRepositoriesButton batchId={batchId} assessmentId={assessmentId} />
          )}
        </div>
        <Summary counts={data.counts} />
        {canGrade && (
          <p className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
            This is a viva: the student hands nothing in, so nothing appears in
            the grading inbox. Sit with each student and record their mark from
            the roster below.
          </p>
        )}
      </header>

      {data.clusters.length > 0 && <Clusters detail={data} onCompare={setPair} />}

      <div className="flex flex-wrap gap-1.5">
        {FILTERS.filter((f) => !f.kinds || f.kinds.includes(data.item.kind)).map((f) => {
          const n = (data.students ?? []).filter((s) => matchesFilter(s, f.key)).length;
          return (
            <Button
              key={f.key}
              variant={filter === f.key ? 'default' : 'outline'}
              size="sm"
              className="h-7 gap-1.5 px-2.5 text-xs"
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              <span className="tabular-nums opacity-70">{n}</span>
            </Button>
          );
        })}
      </div>

      <Card className="overflow-hidden shadow-none">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Student</TableHead>
              {/* Deliberately two columns, never one. */}
              <TableHead>Progress</TableHead>
              <TableHead>How it was done</TableHead>
              <TableHead className="text-right">Effort</TableHead>
              <TableHead className="text-right">Last active</TableHead>
              <TableHead className="w-0" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((s) => (
              <StudentRow
                key={s.user_id}
                student={s}
                detail={data}
                onOpen={() => setViewing({ id: s.user_id, name: s.name })}
                onGrade={canGrade ? () => setGrading({ id: s.user_id, name: s.name }) : undefined}
              />
            ))}
          </TableBody>
        </Table>
        {rows.length === 0 && (
          <p className="p-6 text-center text-sm text-muted-foreground">
            Nobody in this batch matches that filter.
          </p>
        )}
      </Card>

      <WorkArtifactSheet
        batchId={batchId}
        assessmentId={assessmentId}
        student={viewing}
        onClose={() => setViewing(null)}
      />
      <CompareSheet
        batchId={batchId}
        assessmentId={assessmentId}
        pair={pair}
        onClose={() => setPair(null)}
      />
      {canGrade && (
        <VivaGradeDialog
          assessmentId={assessmentId}
          student={grading}
          maxScore={data.item.max_score}
          rubric={data.item.rubric}
          existingScore={data.students.find((s) => s.user_id === grading?.id)?.score}
          onClose={() => setGrading(null)}
          onGraded={() => {
            void queryClient.invalidateQueries({
              queryKey: ['trainer', 'work-item', batchId, assessmentId],
            });
          }}
        />
      )}
    </div>
  );
}

/**
 * Re-reads the commit history behind every hand-in in the batch.
 *
 * On demand rather than nightly: a trainer preparing for a session presses it
 * and waits, where a scheduled job would be up to a day stale on the one
 * morning it matters.
 */
function CheckRepositoriesButton({
  batchId,
  assessmentId,
}: {
  batchId: string;
  assessmentId: string;
}) {
  const queryClient = useQueryClient();

  const run = useMutation({
    mutationFn: async () => (await trainerApi.refreshProvenance(batchId)).data,
    onSuccess: (res) => {
      void queryClient.invalidateQueries({
        queryKey: ['trainer', 'work-item', batchId, assessmentId],
      });
      // A partial run must never be reported as a finished one. Saying how many
      // were skipped is the difference between "nothing to worry about" and
      // "we only got through eleven of them".
      if (res.rate_limited) {
        toast.warning(
          `Checked ${res.checked} before GitHub stopped answering — ${res.skipped} still unchecked.`,
          { description: res.note },
        );
        return;
      }
      toast.success(
        `Checked ${res.checked} hand-in${res.checked === 1 ? '' : 's'} — read ${res.read}.`,
        {
          description:
            res.unreadable > 0
              ? `${res.unreadable} could not be read (private, missing, or not a GitHub link).`
              : undefined,
        },
      );
    },
    onError: () => toast.error('The repository check could not be run.'),
  });

  return (
    <Button
      variant="outline"
      size="sm"
      className="h-7 gap-1.5 px-2.5 text-xs"
      disabled={run.isPending}
      onClick={() => run.mutate()}
    >
      <GitCommitHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
      {run.isPending ? 'Reading GitHub…' : 'Check repositories'}
    </Button>
  );
}

/**
 * The header line. Every number here is a count of people, never a percentage
 * or a grade — a trainer acts on "9 have not started", not on "65% complete".
 */
function Summary({ counts }: { counts: WorkItemDetail['counts'] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <span>
        <span className="font-semibold text-foreground">{counts.done}</span> of {counts.total} done
      </span>
      {counts.in_progress > 0 && <span>{counts.in_progress} in progress</span>}
      {counts.submitted > 0 && <span>{counts.submitted} awaiting grading</span>}
      {counts.not_started > 0 && <span>{counts.not_started} not started</span>}
      {counts.needs_asking > 0 && (
        <span className="font-semibold text-sky-700">{counts.needs_asking} to ask about</span>
      )}
      {/*
        Shown so the signal can be read honestly: "3 to ask about" means
        something different drawn from 40 recorded attempts than from 4.
      */}
      {counts.not_recorded > 0 && <span>{counts.not_recorded} without a record</span>}
    </div>
  );
}

/**
 * The headline over the groups.
 *
 * Leads with same-file when there is one, because it is both the stronger claim
 * and the shorter conversation: "these are the same file" needs no
 * interpretation, where "these share distinctive code" does. Mixing the two
 * under one wording would flatten that difference exactly where it matters.
 */
export function clustersHeadline(clusters: { kind: ClusterKind; names: string[] }[]): string {
  const sameFile = clusters.filter((c) => c.kind === 'same_file');
  const shared = clusters.length - sameFile.length;

  if (sameFile.length > 0) {
    const largest = Math.max(...sameFile.map((c) => c.names.length));
    const head =
      sameFile.length === 1
        ? `${largest} students submitted the same file`
        : `${sameFile.length} groups submitted the same file, the largest ${largest} students`;
    return shared > 0
      ? `${head} · ${shared} more ${shared === 1 ? 'group shares' : 'groups share'} distinctive code`
      : head;
  }
  return clusters.length === 1
    ? 'One group shares distinctive code'
    : `${clusters.length} groups share distinctive code`;
}

/**
 * Similarity, grouped and stated once.
 *
 * Above the table rather than inside it, because a cluster is a fact about
 * several rows at once — repeating it on each member's row would make a group
 * of five look like five separate findings.
 */
function Clusters({
  detail,
  onCompare,
}: {
  detail: WorkItemDetail;
  onCompare: (pair: { a: string; b: string }) => void;
}) {
  return (
    <Card className="space-y-2 border-sky-200 bg-sky-50/50 p-3 shadow-none">
      <p className="text-xs font-semibold text-sky-900">{clustersHeadline(detail.clusters)}</p>
      <ul className="space-y-1.5">
        {detail.clusters.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            <Badge variant="outline" className="border-sky-300 bg-white py-px text-sky-800">
              Group {c.id + 1}
            </Badge>
            <span className="font-medium">{c.names.join(', ')}</span>
            {/*
              Two different findings, two different sentences. "Alike" is the
              honest word for shared structure; for a group whose files match
              down to the variable names, "alike" understates it to the point
              of being misleading.
            */}
            <span className="tabular-nums text-muted-foreground">
              {c.kind === 'same_file'
                ? `${c.names.length} submissions, same file · ${Math.round(c.top_score * 100)}% identical`
                : `${Math.round(c.top_score * 100)}% alike`}
            </span>
            {/*
              The claim and the evidence sit together. A trainer should never
              have to act on "these are alike" without being one click from
              deciding that for themselves.
            */}
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs text-sky-800"
              onClick={() => onCompare({ a: c.members[0], b: c.members[1] })}
            >
              Read both
            </Button>
          </li>
        ))}
      </ul>
      {/*
        Says what was measured and what it does not mean. A college can only
        trust a signal it is allowed to interrogate, and the honest reading of a
        match is "worth a conversation", not "cheated".

        The two channels measure opposite things, and describing only one of
        them would make this note quietly false: "ignoring names and formatting"
        is exactly wrong for a same-file group, where matching names and
        formatting are the whole basis of the claim.
      */}
      <p className="text-[11px] leading-relaxed text-sky-900/70">
        {detail.clusters.some((c) => c.kind === 'same_file') ? (
          <>
            <strong className="font-semibold">Same file</strong> means the submissions match down to
            the variable names, spacing and comments — detail two people do not arrive at
            separately. <strong className="font-semibold">Shares distinctive code</strong> is looser:
            matched on structure alone, ignoring names and formatting. Either can mean copying,
            working together, or one solution everyone read in the same place — both are a prompt to
            ask, not a finding.
          </>
        ) : (
          <>
            Matching is on code structure, ignoring names, comments and formatting. Shared code can
            mean copying, working together, or a solution everyone read in the same place — it is a
            prompt to ask, not a finding.
          </>
        )}
      </p>
    </Card>
  );
}

/**
 * The facts that sit beside a repository signal rather than inside it.
 *
 * Each is a different question, so each gets its own chip. Rolling them into
 * the signal would force a precedence order between "this is a fork" and "this
 * arrived in one commit", and a trainer needs both.
 */
function RepoFlags({ repo }: { repo: WorkRepoProvenance }) {
  const flags: string[] = [];
  if (repo.is_fork) flags.push(repo.fork_parent ? `Fork of ${repo.fork_parent}` : 'Fork');
  if (repo.shared_authorship) {
    flags.push(`${repo.authors?.length ?? 2} commit authors`);
  }
  if (repo.predates_assignment) flags.push('Finished before it was set');
  if (flags.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1">
      {flags.map((f) => (
        <Badge
          key={f}
          variant="outline"
          className="border-sky-200 bg-sky-50 py-px text-[10px] font-normal text-sky-800"
        >
          {f}
        </Badge>
      ))}
    </div>
  );
}

function StudentRow({
  student,
  detail,
  onOpen,
  onGrade,
}: {
  student: WorkItemStudent;
  detail: WorkItemDetail;
  onOpen: () => void;
  /** Only supplied for work a trainer marks in person. */
  onGrade?: () => void;
}) {
  const signal = student.integrity?.signal;
  const cluster = student.cluster_id != null ? detail.clusters[student.cluster_id] : undefined;

  return (
    <TableRow>
      <TableCell className="font-medium">
        {student.name}
        {cluster && (
          <Badge
            variant="outline"
            className="ml-2 border-sky-300 bg-sky-50 py-px text-[10.5px] text-sky-800"
          >
            Group {cluster.id + 1}
          </Badge>
        )}
      </TableCell>

      <TableCell>
        <Badge variant="outline" className={cn('py-px text-[10.5px]', STATUS_TONE[student.status])}>
          {STATUS_LABEL[student.status]}
        </Badge>
        {student.score != null && detail.item.max_score != null && (
          <span className="ml-2 text-xs font-semibold tabular-nums">
            {student.score}/{detail.item.max_score}
          </span>
        )}
      </TableCell>

      <TableCell className="text-xs">
        {hasSignal(student.integrity) && signal ? (
          <div className="space-y-0.5">
            <span className={cn('font-semibold', SIGNAL_TONE[signal])}>
              {SIGNAL_LABEL[signal]}
              {/* Practice: the number behind the label. */}
              {(signal === 'pasted' || signal === 'assisted') && student.integrity && (
                <span className="ml-1 font-normal tabular-nums text-muted-foreground">
                  ({Math.round(student.integrity.largest_paste_ratio * 100)}% in one paste)
                </span>
              )}
              {/* Repository work: the same, in commits. */}
              {student.integrity?.repo && student.integrity.repo.commits > 0 && (
                <span className="ml-1 font-normal tabular-nums text-muted-foreground">
                  ({student.integrity.repo.commits} commit
                  {student.integrity.repo.commits === 1 ? '' : 's'}
                  {student.integrity.repo.span_days > 0 &&
                    ` over ${student.integrity.repo.span_days}d`}
                  )
                </span>
              )}
            </span>
            {student.integrity?.repo && <RepoFlags repo={student.integrity.repo} />}
          </div>
        ) : (
          /*
            Absence stated plainly rather than left blank — an empty cell reads
            as "fine", and this work may predate the telemetry entirely. But
            "not recorded" is only honest where a record was possible: a quiz
            has no code to watch being written, and saying it was not recorded
            would describe a gap that cannot be closed.
          */
          <span className="text-muted-foreground/60">
            {student.status === 'not_started' ? (
              '—'
            ) : detail.item.kind === 'quiz' || detail.item.kind === 'viva' ? (
              // A set of answers has no process, and neither does an answer
              // given out loud. These are the work types where the gap is
              // permanent rather than merely unfilled.
              'Nothing to record'
            ) : student.integrity?.repo?.fetch_error ? (
              // Why we could not look, so the trainer can fix it rather than
              // wonder. Never styled as a finding.
              <span title={student.integrity.repo.fetch_error}>
                {student.integrity.repo.fetch_error}
              </span>
            ) : (
              'Not checked yet'
            )}
          </span>
        )}
      </TableCell>

      <TableCell className="whitespace-nowrap text-right text-xs tabular-nums text-muted-foreground">
        {(student.attempts ?? 0) > 0 && (
          <span>
            {student.attempts} attempt{student.attempts === 1 ? '' : 's'}
          </span>
        )}
        {student.integrity && student.integrity.active_mins > 0 && (
          <span className="ml-2">{student.integrity.active_mins}m</span>
        )}
      </TableCell>

      <TableCell className="whitespace-nowrap text-right text-xs tabular-nums text-muted-foreground">
        {formatWhen(student.last_activity_at) || '—'}
      </TableCell>

      <TableCell className="whitespace-nowrap text-right">
        {/*
          Not offered when there is nothing behind it. A button that opens an
          empty drawer teaches a trainer to stop pressing it.
        */}
        {onGrade ? (
          /*
            Offered for every student on the roster, including the ones with no
            row yet — that is the whole point of a mark with no hand-in behind
            it. "Re-mark" rather than "Mark" once one exists, so nobody
            overwrites a colleague's mark thinking the field was blank.
          */
          <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs" onClick={onGrade}>
            {student.score != null ? 'Re-mark' : 'Record mark'}
          </Button>
        ) : (
          student.status !== 'not_started' && (
            <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs" onClick={onOpen}>
              View work
            </Button>
          )
        )}
      </TableCell>
    </TableRow>
  );
}
